import type { RepwiseEvent } from '@repwise/domain';

/** Wire event, exactly as docs/04-sync-protocol.md §1. */
export interface WireEvent {
  id: string;
  device_id: string;
  device_seq: number;
  type: string;
  workout_id: string | null;
  at: string;
  payload: RepwiseEvent;
  hash: string;
}

export type PushStatus = 'inserted' | 'duplicate' | 'conflict' | 'rejected';
export interface PushResult {
  id: string;
  status: PushStatus;
}

export interface PulledEvent extends WireEvent {
  server_seq: number;
}

/**
 * The seam for storage backends. SupabaseTransport implements it today; a GoogleSheetsTransport writing to
 * the user's own Drive (scope drive.file) can implement the same contract later (§6 of the protocol doc).
 * Implementations throw on transport failure (offline, 5xx, auth); per-row problems come back as statuses.
 */
export interface SyncTransport {
  push(rows: WireEvent[]): Promise<PushResult[]>;
  pull(since: number, limit: number): Promise<PulledEvent[]>;
}

export class TransportError extends Error {
  constructor(
    message: string,
    readonly kind: 'offline' | 'auth' | 'server' = 'server',
  ) {
    super(message);
    this.name = 'TransportError';
  }
}

/** The tiny slice of supabase-js we use, so this package doesn't depend on it. */
export interface RpcClient {
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: { message: string; code?: string; status?: number } | null }>;
}

function classify(err: { message: string; code?: string; status?: number }): TransportError {
  if (err.status === 401 || err.code === 'PGRST301' || /jwt|auth/i.test(err.message)) return new TransportError(err.message, 'auth');
  if (err.status === 0 || /fetch|network|offline/i.test(err.message)) return new TransportError(err.message, 'offline');
  return new TransportError(err.message, 'server');
}

export class SupabaseTransport implements SyncTransport {
  constructor(private readonly client: RpcClient) {}

  async push(rows: WireEvent[]): Promise<PushResult[]> {
    let res;
    try {
      res = await this.client.rpc('sync_push', { p_events: rows });
    } catch (e) {
      throw new TransportError(e instanceof Error ? e.message : String(e), 'offline');
    }
    if (res.error) throw classify(res.error);
    return res.data as PushResult[];
  }

  async pull(since: number, limit: number): Promise<PulledEvent[]> {
    let res;
    try {
      res = await this.client.rpc('sync_pull', { p_since: since, p_limit: limit });
    } catch (e) {
      throw new TransportError(e instanceof Error ? e.message : String(e), 'offline');
    }
    if (res.error) throw classify(res.error);
    return (res.data as PulledEvent[]).map((r) => ({ ...r, server_seq: Number(r.server_seq) }));
  }
}

/**
 * In-memory server with the same semantics as supabase/migrations (idempotent by id, conflicts never
 * overwrite, per-user seq). Used by tests and for running the app without a backend.
 */
export class MemoryServer {
  private rows = new Map<string, PulledEvent & { user: string }>();
  private seq = 0;
  /** Set to make the next N push calls apply their rows and then fail (lost response). */
  loseResponses = 0;
  offline = false;
  pushCalls = 0;

  transport(getUser: () => string | null): SyncTransport {
    return {
      push: async (rows) => {
        this.pushCalls++;
        if (this.offline) throw new TransportError('offline', 'offline');
        const user = getUser();
        if (!user) throw new TransportError('no session', 'auth');
        if (rows.length > 500) throw new TransportError('batch too large', 'server');
        const out = rows.map((r): PushResult => {
          const existing = this.rows.get(r.id);
          if (!/^[0-9a-f]{64}$/.test(r.hash) || !/^[a-z_]{1,40}$/.test(r.type)) return { id: r.id, status: 'rejected' };
          if (existing) {
            if (existing.user !== user) return { id: r.id, status: 'rejected' };
            return { id: r.id, status: existing.hash === r.hash ? 'duplicate' : 'conflict' };
          }
          this.rows.set(r.id, { ...r, server_seq: ++this.seq, user });
          return { id: r.id, status: 'inserted' };
        });
        if (this.loseResponses > 0) {
          this.loseResponses--;
          throw new TransportError('response lost', 'offline');
        }
        return out;
      },
      pull: async (since, limit) => {
        if (this.offline) throw new TransportError('offline', 'offline');
        const user = getUser();
        if (!user) throw new TransportError('no session', 'auth');
        return [...this.rows.values()]
          .filter((r) => r.user === user && r.server_seq > since)
          .sort((a, b) => a.server_seq - b.server_seq)
          .slice(0, limit)
          .map(({ user: _u, ...r }) => r);
      },
    };
  }

  count(user?: string): number {
    return [...this.rows.values()].filter((r) => !user || r.user === user).length;
  }

  /** Test hook: plant a row as if another device wrote it. */
  plant(user: string, row: WireEvent): void {
    this.rows.set(row.id, { ...row, server_seq: ++this.seq, user });
  }
}
